// ===========================================
// Project Context - Global Project State
// ===========================================

import React, { createContext, useContext, useState, useCallback } from 'react';
import { projectAPI } from '../services/api';

// Create the context
const ProjectContext = createContext(null);

// ===========================================
// Project Provider Component
// ===========================================
export const ProjectProvider = ({ children }) => {
  // Projects list state
  const [projects, setProjects] = useState([]);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    pages: 0,
    hasMore: false
  });
  
  // Current/focused project
  const [currentProject, setCurrentProject] = useState(null);
  
  // Loading and error states
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // ===========================================
  // Fetch projects list
  // ===========================================
  const fetchProjects = useCallback(async (filters = {}) => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await projectAPI.list(filters);
      const { projects: projectList, pagination: paginationData } = response.data.data;
      
      setProjects(projectList);
      setPagination(paginationData);
      setLoading(false);
      
      return { success: true, data: projectList };
    } catch (err) {
      const message = err.response?.data?.error?.message || 'Failed to fetch projects';
      setError(message);
      setLoading(false);
      return { success: false, error: message };
    }
  }, []);

  // ===========================================
  // Get single project
  // ===========================================
  const getProject = useCallback(async (id) => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await projectAPI.get(id);
      const project = response.data.data.project;
      
      setCurrentProject(project);
      setLoading(false);
      
      return { success: true, data: project };
    } catch (err) {
      const message = err.response?.data?.error?.message || 'Failed to fetch project';
      setError(message);
      setLoading(false);
      return { success: false, error: message };
    }
  }, []);

  // ===========================================
  // Create new project
  // ===========================================
  const createProject = useCallback(async (data) => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await projectAPI.create(data);
      const newProject = response.data.data.project;
      
      // Add to projects list
      setProjects(prev => [newProject, ...prev]);
      setLoading(false);
      
      return { success: true, data: newProject };
    } catch (err) {
      const message = err.response?.data?.error?.message || 'Failed to create project';
      setError(message);
      setLoading(false);
      return { success: false, error: message };
    }
  }, []);

  // ===========================================
  // Update project
  // ===========================================
  const updateProject = useCallback(async (id, data) => {
    setError(null);
    
    try {
      const response = await projectAPI.update(id, data);
      const updatedProject = response.data.data.project;
      
      // Update in projects list - use both _id and id for MongoDB compatibility
      setProjects(prev => prev.map(p => 
        (p._id || p.id) === id ? { ...p, ...updatedProject } : p
      ));
      
      // Update current project if it's the one being edited
      if (currentProject && (currentProject._id || currentProject.id) === id) {
        setCurrentProject(updatedProject);
      }
      
      return { success: true, data: updatedProject };
    } catch (err) {
      const message = err.response?.data?.error?.message || 'Failed to update project';
      setError(message);
      return { success: false, error: message };
    }
  }, [currentProject]);

  // ===========================================
  // Delete (archive) project
  // ===========================================
  const deleteProject = useCallback(async (id) => {
    setError(null);
    
    try {
      await projectAPI.delete(id);
      
      // Remove from projects list - use both _id and id for MongoDB compatibility
      setProjects(prev => prev.filter(p => (p._id || p.id) !== id));
      
      // Clear current project if it was deleted
      if (currentProject && (currentProject._id || currentProject.id) === id) {
        setCurrentProject(null);
      }
      
      return { success: true };
    } catch (err) {
      const message = err.response?.data?.error?.message || 'Failed to delete project';
      setError(message);
      return { success: false, error: message };
    }
  }, [currentProject]);

  // ===========================================
  // Permanently Delete Project
  // ===========================================
  // Requires password verification before calling - handled in UI
  const permanentDeleteProject = useCallback(async (id) => {
    setError(null);
    
    try {
      await projectAPI.permanentDelete(id);
      
      // Remove from projects list - use both _id and id for MongoDB compatibility
      setProjects(prev => prev.filter(p => (p._id || p.id) !== id));
      
      // Clear current project if it was deleted
      if (currentProject && (currentProject._id || currentProject.id) === id) {
        setCurrentProject(null);
      }
      
      return { success: true };
    } catch (err) {
      const message = err.response?.data?.error?.message || 'Failed to permanently delete project';
      setError(message);
      return { success: false, error: message };
    }
  }, [currentProject]);

  // ===========================================
  // Focus on a project
  // ===========================================
  const focusProject = useCallback(async (id, duration = 0) => {
    try {
      await projectAPI.focus(id, duration);
      
      // Update last focused in list - use both _id and id for MongoDB compatibility
      setProjects(prev => prev.map(p => 
        (p._id || p.id) === id 
          ? { ...p, lastFocused: new Date().toISOString() } 
          : p
      ));
      
      return { success: true };
    } catch (err) {
      const message = err.response?.data?.error?.message || 'Failed to focus project';
      setError(message);
      return { success: false, error: message };
    }
  }, []);

  // ===========================================
  // Service Management
  // ===========================================
  const addService = useCallback(async (projectId, serviceData) => {
    try {
      const response = await projectAPI.addService(projectId, serviceData);
      const { service, totalMonthlyCost } = response.data.data;
      
      // Update current project if it's the one being modified
      if (currentProject && (currentProject._id || currentProject.id) === projectId) {
        setCurrentProject(prev => ({
          ...prev,
          services: [...(prev.services || []), service],
          totalMonthlyCost
        }));
      }
      
      return { success: true, data: service };
    } catch (err) {
      const message = err.response?.data?.error?.message || 'Failed to add service';
      return { success: false, error: message };
    }
  }, [currentProject]);

  // Update an existing service
  const updateService = useCallback(async (projectId, serviceId, serviceData) => {
    try {
      const response = await projectAPI.updateService(projectId, serviceId, serviceData);
      const { service, totalMonthlyCost } = response.data.data;
      
      // Update the service in local state
      if (currentProject && (currentProject._id || currentProject.id) === projectId) {
        setCurrentProject(prev => ({
          ...prev,
          services: prev.services.map(s => 
            (s._id || s.id) === serviceId ? { ...s, ...service } : s
          ),
          totalMonthlyCost
        }));
      }
      
      return { success: true, data: service };
    } catch (err) {
      const message = err.response?.data?.error?.message || 'Failed to update service';
      return { success: false, error: message };
    }
  }, [currentProject]);

  const deleteService = useCallback(async (projectId, serviceId) => {
    try {
      const response = await projectAPI.deleteService(projectId, serviceId);
      const { totalMonthlyCost } = response.data.data;
      
      if (currentProject && (currentProject._id || currentProject.id) === projectId) {
        setCurrentProject(prev => ({
          ...prev,
          services: prev.services.filter(s => (s._id || s.id) !== serviceId),
          totalMonthlyCost
        }));
      }
      
      return { success: true };
    } catch (err) {
      const message = err.response?.data?.error?.message || 'Failed to delete service';
      return { success: false, error: message };
    }
  }, [currentProject]);

  // ===========================================
  // Environment Variables
  // ===========================================
  const addEnvVariable = useCallback(async (projectId, envData) => {
    try {
      const response = await projectAPI.addEnvVariable(projectId, envData);
      const envVar = response.data.data.envVariable;
      
      if (currentProject && (currentProject._id || currentProject.id) === projectId) {
        setCurrentProject(prev => ({
          ...prev,
          envVariables: [...(prev.envVariables || []), envVar]
        }));
      }
      
      return { success: true, data: envVar };
    } catch (err) {
      const message = err.response?.data?.error?.message || 'Failed to add environment variable';
      return { success: false, error: message };
    }
  }, [currentProject]);

  // Update an existing environment variable
  const updateEnvVariable = useCallback(async (projectId, envId, envData) => {
    try {
      const response = await projectAPI.updateEnvVariable(projectId, envId, envData);
      const updatedEnv = response.data.data.envVariable;
      
      // Update the env variable in local state
      if (currentProject && (currentProject._id || currentProject.id) === projectId) {
        setCurrentProject(prev => ({
          ...prev,
          envVariables: prev.envVariables.map(e => 
            (e._id || e.id) === envId ? { ...e, ...updatedEnv } : e
          )
        }));
      }
      
      return { success: true, data: updatedEnv };
    } catch (err) {
      const message = err.response?.data?.error?.message || 'Failed to update environment variable';
      return { success: false, error: message };
    }
  }, [currentProject]);

  const deleteEnvVariable = useCallback(async (projectId, envId) => {
    try {
      await projectAPI.deleteEnvVariable(projectId, envId);
      
      if (currentProject && (currentProject._id || currentProject.id) === projectId) {
        setCurrentProject(prev => ({
          ...prev,
          envVariables: prev.envVariables.filter(e => (e._id || e.id) !== envId)
        }));
      }
      
      return { success: true };
    } catch (err) {
      const message = err.response?.data?.error?.message || 'Failed to delete environment variable';
      return { success: false, error: message };
    }
  }, [currentProject]);

  // ===========================================
  // Roadmap
  // ===========================================
  const addMilestone = useCallback(async (projectId, milestoneData) => {
    try {
      const response = await projectAPI.addMilestone(projectId, milestoneData);
      const milestone = response.data.data.milestone;
      
      if (currentProject && (currentProject._id || currentProject.id) === projectId) {
        setCurrentProject(prev => ({
          ...prev,
          roadmap: [...(prev.roadmap || []), milestone]
        }));
      }
      
      return { success: true, data: milestone };
    } catch (err) {
      const message = err.response?.data?.error?.message || 'Failed to add milestone';
      return { success: false, error: message };
    }
  }, [currentProject]);

  const updateMilestone = useCallback(async (projectId, milestoneId, data) => {
    try {
      const response = await projectAPI.updateMilestone(projectId, milestoneId, data);
      const milestone = response.data.data.milestone;
      
      if (currentProject && (currentProject._id || currentProject.id) === projectId) {
        setCurrentProject(prev => ({
          ...prev,
          roadmap: prev.roadmap.map(m => (m._id || m.id) === milestoneId ? milestone : m)
        }));
      }
      
      return { success: true, data: milestone };
    } catch (err) {
      const message = err.response?.data?.error?.message || 'Failed to update milestone';
      return { success: false, error: message };
    }
  }, [currentProject]);

  // Delete milestone from roadmap
  const deleteMilestone = useCallback(async (projectId, milestoneId) => {
    try {
      await projectAPI.deleteMilestone(projectId, milestoneId);
      
      // Remove milestone from local state
      if (currentProject && (currentProject._id || currentProject.id) === projectId) {
        setCurrentProject(prev => ({
          ...prev,
          roadmap: prev.roadmap.filter(m => (m._id || m.id) !== milestoneId)
        }));
      }
      
      return { success: true };
    } catch (err) {
      const message = err.response?.data?.error?.message || 'Failed to delete milestone';
      return { success: false, error: message };
    }
  }, [currentProject]);

  // ===========================================
  // Clear states
  // ===========================================
  const clearCurrentProject = useCallback(() => {
    setCurrentProject(null);
  }, []);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // Context value
  const value = {
    // State
    projects,
    currentProject,
    pagination,
    loading,
    error,
    
    // Project CRUD
    fetchProjects,
    getProject,
    createProject,
    updateProject,
    deleteProject,
    permanentDeleteProject,
    focusProject,
    
    // Services
    addService,
    updateService,
    deleteService,
    
    // Environment Variables
    addEnvVariable,
    updateEnvVariable,
    deleteEnvVariable,
    
    // Roadmap
    addMilestone,
    updateMilestone,
    deleteMilestone,
    
    // Utilities
    clearCurrentProject,
    clearError,
    setCurrentProject
  };

  return (
    <ProjectContext.Provider value={value}>
      {children}
    </ProjectContext.Provider>
  );
};

// ===========================================
// Custom hook for using project context
// ===========================================
export const useProjects = () => {
  const context = useContext(ProjectContext);
  
  if (!context) {
    throw new Error('useProjects must be used within a ProjectProvider');
  }
  
  return context;
};

export default ProjectContext;

